// src/utils/permissions.js
// Utility functions for permission checking

/**
 * Check if a user has a specific permission
 * @param {Object} user - The user object from useApiAuth
 * @param {string} permission - The permission to check
 * @returns {boolean} - True if user has the permission
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;
  
  // Admin has all permissions
  if (user.role === 'admin') return true;
  
  // Support both legacy array and new permission map payloads
  const userPermissions = user.permissions || {};
  if (Array.isArray(userPermissions)) {
    return userPermissions.includes(permission);
  }

  return userPermissions[permission] === true;
};

/**
 * Check if a user has any of the specified permissions
 * @param {Object} user - The user object from useApiAuth
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} - True if user has any of the permissions
 */
export const hasAnyPermission = (user, permissions) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  
  const userPermissions = user.permissions || {};
  if (Array.isArray(userPermissions)) {
    return permissions.some(permission => userPermissions.includes(permission));
  }

  return permissions.some(permission => userPermissions[permission] === true);
};

/**
 * Check if a user has all of the specified permissions
 * @param {Object} user - The user object from useApiAuth
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} - True if user has all of the permissions
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  
  const userPermissions = user.permissions || {};
  if (Array.isArray(userPermissions)) {
    return permissions.every(permission => userPermissions.includes(permission));
  }

  return permissions.every(permission => userPermissions[permission] === true);
};

/**
 * Check if a user has a specific role
 * @param {Object} user - The user object from useApiAuth
 * @param {string} role - The role to check
 * @returns {boolean} - True if user has the role
 */
export const hasRole = (user, role) => {
  if (!user) return false;
  return user.role === role;
};

/**
 * Check if a user has any of the specified roles
 * @param {Object} user - The user object from useApiAuth
 * @param {string[]} roles - Array of roles to check
 * @returns {boolean} - True if user has any of the roles
 */
export const hasAnyRole = (user, roles) => {
  if (!user) return false;
  return roles.includes(user.role);
};
